import axios from "axios";
import { authArgs, getApiContext, normalizeToken } from "./requestAuth";

const pendingGuidelineReads = new Map();

const notifyBrandGuidelineUpdated = () => {
  pendingGuidelineReads.clear();
  window.dispatchEvent(new Event("brand-guideline-updated"));
};

export const getBrandGuideline = (orgId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const effectiveToken = normalizeToken(tokenOverride || token);
  const key = JSON.stringify([apiUrl, orgId, effectiveToken]);
  if (pendingGuidelineReads.has(key)) return pendingGuidelineReads.get(key);

  // Share concurrent reads from the navbar reminder and the page, but never
  // retain a settled response that could populate an editor with stale data.
  const request = axios.get(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline`,
    ...authArgs(effectiveToken)
  ).then((response) => response.data).finally(() => {
    if (pendingGuidelineReads.get(key) === request) pendingGuidelineReads.delete(key);
  });
  pendingGuidelineReads.set(key, request);
  return request;
};

export const getBrandGuidelinePrivilegedStatus = async (orgId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.get(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline/privileged-status`,
    ...authArgs(tokenOverride || token)
  );
  return response.data;
};

export const getLoadableBrandGuidelineOrganizations = async (orgId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.get(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline/loadable-organizations`,
    ...authArgs(tokenOverride || token)
  );
  return response.data;
};

export const getLoadableBrandGuidelinePreview = async (orgId, sourceOrganizationId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.get(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline/loadable-organizations/${sourceOrganizationId}`,
    ...authArgs(tokenOverride || token)
  );
  return response.data;
};

export const loadBrandGuideline = async (orgId, sourceOrganizationId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.post(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline/load`,
    { source_organization_id: sourceOrganizationId },
    ...authArgs(tokenOverride || token)
  );
  notifyBrandGuidelineUpdated();
  return response.data;
};

export const updateBrandGuideline = async (orgId, guideline, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.put(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline`,
    guideline,
    ...authArgs(tokenOverride || token)
  );
  notifyBrandGuidelineUpdated();
  return response.data;
};

export const uploadBrandReference = async (orgId, file, isLogo = false) => {
  const { apiUrl, token } = getApiContext();
  const body = new FormData(); body.append("image", file);
  const suffix = isLogo ? "logo" : "reference-images";
  const response = await axios.post(`${apiUrl}/api/organizations/${orgId}/brand-guideline/${suffix}`, body, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
  notifyBrandGuidelineUpdated();
  return response.data;
};

export const deleteBrandReference = async (orgId, imageId) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.delete(`${apiUrl}/api/organizations/${orgId}/brand-guideline/reference-images/${imageId}`, ...authArgs(token));
  notifyBrandGuidelineUpdated();
  return response;
};

const uploadBrandGuidelineDocument = async (orgId, file, path, tokenOverride, replaceMissing = false) => {
  const { apiUrl, token } = getApiContext();
  const body = new FormData();
  body.append("file", file);
  body.append("replace_missing", String(replaceMissing));
  const response = await axios.post(`${apiUrl}/api/organizations/${orgId}/brand-guideline/${path}`, body, {
    headers: { ...(tokenOverride || token ? { Authorization: `Bearer ${tokenOverride || token}` } : {}), "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
  return response.data;
};

export const previewBrandGuidelineDocument = (orgId, file, tokenOverride, replaceMissing = false) =>
  uploadBrandGuidelineDocument(orgId, file, "import-preview", tokenOverride, replaceMissing);

export const importBrandGuidelineDocument = async (orgId, file, tokenOverride, replaceMissing = false) => {
  const result = await uploadBrandGuidelineDocument(orgId, file, "import", tokenOverride, replaceMissing);
  notifyBrandGuidelineUpdated();
  return result;
};

export const previewGeneratedBrandGuideline = async (orgId, logo, websiteUrl, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const body = new FormData();
  body.append("logo", logo);
  body.append("website_url", websiteUrl);
  const response = await axios.post(`${apiUrl}/api/organizations/${orgId}/brand-guideline/generate-preview`, body, {
    headers: { ...(tokenOverride || token ? { Authorization: `Bearer ${tokenOverride || token}` } : {}), "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
  return response.data;
};

export const confirmGeneratedBrandGuideline = async (orgId, candidateId, tokenOverride) => {
  const { apiUrl, token } = getApiContext();
  const response = await axios.post(
    `${apiUrl}/api/organizations/${orgId}/brand-guideline/generate-confirm`,
    { candidate_id: candidateId },
    ...authArgs(tokenOverride || token)
  );
  notifyBrandGuidelineUpdated();
  return response.data;
};
